import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowUpRightIcon, FilePlusIcon, WrenchIcon } from 'lucide-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { PageTitle } from '@/components/layout/page-title'
import { Spinner } from '@/components/spinner'
import { useDexieDb } from '@/db/dexie-context'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { createDraft } from '@/features/interventi/save-intervento'
import { fetchShortId } from '@/features/interventi/utils/short-id'

export const Route = createFileRoute('/_authenticated/interventi/nuovo')({
  component: RouteComponent,
})

// Two large targets that share a phone's screen (side by side on a desktop), on
// the info panel's light surface; the icon carries the colour of the Stato it
// leads to (In corso, In riparazione). The whole tile answers the tap
const tileClass =
  'group relative flex min-h-44 flex-col justify-between overflow-hidden rounded-[1.35rem] border border-slate-200/80 bg-[linear-gradient(135deg,rgba(248,250,252,0.98),rgba(241,245,249,0.9))] p-6 text-left shadow-sm transition duration-150 hover:border-slate-300 hover:shadow-md active:scale-[0.98] focus-visible:ring-4 focus-visible:ring-slate-300 focus-visible:outline-none disabled:cursor-wait disabled:opacity-80 md:min-h-72 md:p-8'
const iconClass = 'flex size-14 items-center justify-center rounded-2xl border'

/**
 * What to do: inspect a drain (a new intervento, saved as a draft at once so
 * its ID is never lost) or repair one already inspected.
 */
function RouteComponent() {
  const db = useDexieDb()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const newIntervento = async () => {
    if (!user?.id) return
    setCreating(true)
    try {
      const draft = await createDraft(db, {
        idInterno: await fetchShortId(),
        userId: user.id,
      })
      // Back from the draft goes to the list, not here again
      await navigate({
        to: '/interventi/modifica/$interventoId',
        params: { interventoId: draft.id },
        replace: true,
      })
    } catch (error) {
      toast.error('Si è verificato un errore')
      console.error(error)
      setCreating(false)
    }
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { name: 'Interventi', to: '/interventi' },
          { name: 'Nuovo', to: '/interventi/nuovo' },
        ]}
      />

      <div className="flex flex-col md:max-w-4xl">
        <PageTitle className="mb-6">Cosa devi fare?</PageTitle>

        {/* On a phone: the height between the title and the bottom bar */}
        <div className="grid min-h-[calc(100dvh-15rem)] grid-rows-2 gap-3 md:min-h-0 md:grid-cols-2 md:grid-rows-1 md:gap-5">
          <button
            type="button"
            className={tileClass}
            onClick={newIntervento}
            disabled={creating}
          >
            <span className="flex items-start justify-between">
              <span
                className={cn(
                  iconClass,
                  'border-sky-200 bg-sky-50 text-sky-700'
                )}
              >
                {creating ? (
                  <Spinner size="small" className="text-sky-700" />
                ) : (
                  <FilePlusIcon className="size-7" />
                )}
              </span>
              <ArrowUpRightIcon className="size-6 text-slate-400 transition-colors group-hover:text-slate-700" />
            </span>
            <span className="space-y-1.5">
              <span className="block text-2xl font-semibold tracking-tight text-slate-900">
                Nuovo intervento
              </span>
              <span className="block max-w-xs text-sm text-slate-500">
                Ispeziona una caditoia.
              </span>
            </span>
          </button>

          <Link
            to="/interventi/riparazione"
            className={tileClass}
            disabled={creating}
          >
            <span className="flex items-start justify-between">
              <span
                className={cn(
                  iconClass,
                  'border-amber-200 bg-amber-50 text-amber-700'
                )}
              >
                <WrenchIcon className="size-7" />
              </span>
              <ArrowUpRightIcon className="size-6 text-slate-400 transition-colors group-hover:text-slate-700" />
            </span>
            <span className="space-y-1.5">
              <span className="block text-2xl font-semibold tracking-tight text-slate-900">
                Riparazione
              </span>
              <span className="block max-w-xs text-sm text-slate-500">
                Trova una caditoia con esito negativo e riparala.
              </span>
            </span>
          </Link>
        </div>
      </div>
    </>
  )
}
